import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { BehaviorSubject, map, mergeMap, Observable } from 'rxjs';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { DiscountType } from 'src/app/shared/enums/discount-type.enum';
import { ErrorMessage } from 'src/app/shared/models/errorMessage.model';
import { EnumToArrayPipe } from 'src/app/shared/pipes/enum-to-array.pipe';
import { TranslateOrganizationDiscountTypeEnum } from 'src/app/shared/pipes/translate-organization-discount-type-enum.pipe';
import {
  AccessGroupSharedDiscountSearchEvent,
  AccessGroupSharedDiscountService,
  CreateAccessGroupSharedDiscountRequest,
  EditAccessGroupSharedDiscountRequest,
  IAccessGroupSharedDiscountSearchResultItem,
} from 'src/app/shared/services/access-group-shared-discount.service';
import { SnackbarService } from 'ok-angular-shared/components';
import { CommonModule } from '@angular/common';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { DotToCommaPipe } from '../../../shared/pipes/dot-to-comma.pipe';

export interface IAccessGroupSharedDiscountForm {
  name: FormControl<string>;
  amount: FormControl<number | null>;
  discountType: FormControl<DiscountType>;
}

@Component({
  selector: 'adm-create-or-edit-access-group-shared-discount-modal',
  imports: [
    CustomDropDownArrowDirective,
    EnumToArrayPipe,
    TranslateOrganizationDiscountTypeEnum,
    CommonModule,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    ReactiveFormsModule,
    RouterModule,
    DotToCommaPipe,
  ],
  templateUrl: './create-or-edit-access-group-shared-discount-modal.component.html',
  styleUrl: './create-or-edit-access-group-shared-discount-modal.component.scss',
})
export class CreateOrEditAccessGroupSharedDiscountModalComponent implements OnInit {
  accessGroupSharedDiscountService = inject(AccessGroupSharedDiscountService);
  snackbarService = inject(SnackbarService);
  dialog: MatDialog = inject(MatDialog);
  public discounts$: Observable<IAccessGroupSharedDiscountSearchResultItem[]>;
  public updateSubject$ = new BehaviorSubject(null);
  public discountTypes: typeof DiscountType = DiscountType;
  public discountGroupBeingEdited: IAccessGroupSharedDiscountSearchResultItem | null;

  form: FormGroup = new FormGroup<IAccessGroupSharedDiscountForm>({
    name: new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
    amount: new FormControl<number | null>(null, {
      validators: [Validators.required, Validators.max(100), Validators.min(0)],
      nonNullable: false,
    }),
    discountType: new FormControl<DiscountType>(DiscountType.FixedAmount, { validators: [Validators.required], nonNullable: true }),
  });
  ngOnInit(): void {
    this.discounts$ = this.updateSubject$.pipe(
      mergeMap(() =>
        this.accessGroupSharedDiscountService
          .getSharedDiscounts(new AccessGroupSharedDiscountSearchEvent(<PageEvent>{ pageIndex: 0, pageSize: 100 }))
          .pipe(map(discounts => discounts.items))
      )
    );
  }
  edit(discount: IAccessGroupSharedDiscountSearchResultItem) {
    this.discountGroupBeingEdited = discount;
    this.form.patchValue({
      name: discount.name,
      amount: discount.amount,
      discountType: discount.discount_type,
    });
    this.form.controls.discountType.disable({ onlySelf: true, emitEvent: false });
  }

  create() {
    this.accessGroupSharedDiscountService
      .create(<CreateAccessGroupSharedDiscountRequest>{
        name: this.form.value.name,
        discount_type: this.form.value.discountType,
        amount: this.form.value.amount,
      })
      .subscribe({
        next: () => {
          this.form.reset();
          this.updateSubject$.next(null);
          this.snackbarService.success('Tværgående rabat blev oprettet');
        },
        error: (httpError: HttpErrorResponse) => {
          if (httpError.status === HttpStatusCode.Conflict) {
            const error = httpError.error as ErrorMessage;
            if (error.code === 15100) {
              this.snackbarService.warning(`Der findes allerede en tværgående rabat med navn ${this.form.value.name}`);
            }
          }
        },
      });
  }
  update() {
    if (this.discountGroupBeingEdited) {
      this.accessGroupSharedDiscountService
        .edit(this.discountGroupBeingEdited.identifier, <EditAccessGroupSharedDiscountRequest>{
          name: this.form.value.name,
          amount: this.form.value.amount,
        })
        .subscribe({
          next: () => {
            this.discountGroupBeingEdited = null;
            this.form.reset();
            this.updateSubject$.next(null);
            this.snackbarService.success('Tværgående rabat blev opdateret');
            this.form.controls.discountType.enable({ onlySelf: true, emitEvent: false });
          },
          error: (httpError: HttpErrorResponse) => {
            if (httpError.status === HttpStatusCode.Conflict) {
              const error = httpError.error as ErrorMessage;
              if (error.code === 15100) {
                this.snackbarService.warning(`Der findes allerede en tværgående rabat med navn ${this.form.value.name}`);
              }
            }
            if (httpError.status === HttpStatusCode.BadRequest) {
              const error = httpError.error as ErrorMessage;
              this.snackbarService.warning(error.message);
            }
          },
        });
    }
  }

  remove(discount: IAccessGroupSharedDiscountSearchResultItem) {
    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: `Slet rabat?`,
        text: `Er du sikker på du vil slette rabat '${discount.name}'?`,
        confirmText: `Ja`,
        denyText: `Nej`,
      },
      disableClose: true,
    });

    confirmDialogRef.afterClosed().subscribe(doDelete => {
      if (doDelete) {
        this.accessGroupSharedDiscountService.delete(discount.identifier).subscribe({
          next: () => {
            this.updateSubject$.next(null);
            this.snackbarService.success('Tværgående rabat blev slettet');
          },
          error: (httpError: HttpErrorResponse) => {
            if (httpError.status === HttpStatusCode.BadRequest) {
              const error = httpError.error as ErrorMessage;
              this.snackbarService.warning(error.message);
            }
          },
        });
      }
    });
  }
}
